/**
 * useSwipeActions — touch swipe gesture detector for mobile.
 *
 * Returns handlers that attach to any DOM element. Detects a predominantly
 * horizontal swipe past a threshold and fires the matching callback.
 * Vertical movement or taps pass through untouched.
 */
import { useCallback, useRef } from 'react';

const THRESHOLD = 80;       // px of horizontal travel to trigger
const MAX_VERTICAL = 30;    // px — reject if the user is scrolling vertically

export function useSwipeActions({ onSwipeLeft, onSwipeRight, enabled = true } = {}) {
  const startX = useRef(null);
  const startY = useRef(null);
  const tracking = useRef(false);

  const onTouchStart = useCallback((e) => {
    if (!enabled) return;
    if (!e.touches || e.touches.length !== 1) return;
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    tracking.current = true;
  }, [enabled]);

  const onTouchMove = useCallback((e) => {
    if (!tracking.current || !enabled) return;
    const dy = Math.abs(e.touches[0].clientY - (startY.current ?? 0));
    if (dy > MAX_VERTICAL) {
      // Treat as a vertical scroll — abort swipe tracking.
      tracking.current = false;
    }
  }, [enabled]);

  const onTouchEnd = useCallback((e) => {
    if (!tracking.current || !enabled) { tracking.current = false; return; }
    tracking.current = false;
    const endX = e.changedTouches?.[0]?.clientX ?? startX.current ?? 0;
    const endY = e.changedTouches?.[0]?.clientY ?? startY.current ?? 0;
    const dx = endX - (startX.current ?? 0);
    const dy = Math.abs(endY - (startY.current ?? 0));
    startX.current = null;
    startY.current = null;
    if (dy > MAX_VERTICAL) return;        // too much vertical — ignore
    if (Math.abs(dx) < THRESHOLD) return; // not enough horizontal
    if (dx > 0) {
      onSwipeRight?.(e);
    } else {
      onSwipeLeft?.(e);
    }
  }, [onSwipeLeft, onSwipeRight, enabled]);

  return { onTouchStart, onTouchMove, onTouchEnd };
}
